import { dynamicPackageAttractionLineBillableToCustomer } from "../packageAttractionRulesDisplay.js";

/**
 * חוזה תמחור אחיד לאטרקציות במסע הלקוח (חבילה / הצעה / סיכום / לינק).
 *
 * כללי מוצר:
 * - אם אין price_child במקור (או null / ריק) — ילד לא זכאי (childEligible=false).
 * - אם price_child קיים (כולל 0) — זה המחיר לילד; 0 = מותר ללא עלות.
 * - שמות שדות חלופיים מה-API (למשל dynamic_package_addon_price_child_ils) נחשבים SSOT כמו price_child.
 * - אין fallback ממחיר מבוגר לילד (במיוחד לא 50%).
 */

/**
 * @param {Record<string, unknown>|null|undefined} obj
 * @param {string} snake
 * @param {string} camel
 * @returns {{ found: boolean, value: unknown }}
 */
function readOwnPriceField(obj, snake, camel) {
  if (!obj || typeof obj !== "object") return { found: false, value: undefined };
  if (Object.prototype.hasOwnProperty.call(obj, snake)) {
    return { found: true, value: obj[snake] };
  }
  if (Object.prototype.hasOwnProperty.call(obj, camel)) {
    return { found: true, value: obj[camel] };
  }
  return { found: false, value: undefined };
}

/** מפתחות חלופיים ל-price_child — מקבילים ל-adultKeys ב-resolveAdultChildUnitPricesIls */
const CHILD_PRICE_ALIAS_PAIRS = [
  ["child_price", "childPrice"],
  ["dynamic_package_addon_price_child_ils", "dynamicPackageAddonPriceChildIls"],
  ["unit_price_child_ils", "unitPriceChildIls"],
  ["retail_price_child_ils", "retailPriceChildIls"],
];

/**
 * @param {Record<string, unknown>} obj
 * @returns {{ found: boolean, value: number|null }}
 */
function readChildPriceAliases(obj) {
  for (const [snake, camel] of CHILD_PRICE_ALIAS_PAIRS) {
    const { found, value } = readOwnPriceField(obj, snake, camel);
    if (!found) continue;
    if (value === null || value === undefined || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) {
      return { found: true, value: n };
    }
  }
  return { found: false, value: null };
}

/**
 * קובע האם ילד זכאי ומה מחיר היחידה לילד.
 * סדר עדיפות: merged (איחוד שכבות כולל pricing מקונן) → קטלוג → חוק.
 * merged לפני catalog — כדי שלא ייקלעו price_child:null slim לפני ערכים שחולצו מ-pricing/alias.
 *
 * @param {Record<string, unknown>|null|undefined} rule
 * @param {Record<string, unknown>} merged
 * @param {Record<string, unknown>|null|undefined} catalog
 * @returns {{ childEligible: boolean, childUnitPrice: number|null }}
 */
export function resolveChildUnitFromSources(rule, merged, catalog) {
  const sources = [merged, catalog, rule].filter((s) => s && typeof s === "object");
  for (const src of sources) {
    const o = /** @type {Record<string, unknown>} */ (src);
    const { found, value } = readOwnPriceField(o, "price_child", "priceChild");
    if (found) {
      if (value !== null && value !== undefined && value !== "") {
        const n = Number(value);
        if (Number.isFinite(n) && n >= 0) {
          return { childEligible: true, childUnitPrice: n };
        }
        const aliasBadPrimary = readChildPriceAliases(o);
        if (aliasBadPrimary.found) {
          return { childEligible: true, childUnitPrice: aliasBadPrimary.value };
        }
        continue;
      }
      const aliasAfterNull = readChildPriceAliases(o);
      if (aliasAfterNull.found) {
        return { childEligible: true, childUnitPrice: aliasAfterNull.value };
      }
      continue;
    }
    const alias = readChildPriceAliases(o);
    if (alias.found) {
      return { childEligible: true, childUnitPrice: alias.value };
    }
  }
  return { childEligible: false, childUnitPrice: null };
}

/**
 * סכום שורה לאטרקציה אופציונלית (ILS).
 * @param {{
 *   lineLocked: boolean,
 *   line: Record<string, unknown>|null|undefined,
 *   adultUnit: number|null,
 *   childUnit: number|null,
 *   childEligible: boolean,
 *   adults: number,
 *   children: number,
 * }} p
 */
export function optionalAttractionLineSubtotalIls(p) {
  if (
    p.lineLocked &&
    p.line &&
    dynamicPackageAttractionLineBillableToCustomer(p.line)
  ) {
    return Number(p.line.total_gross) || 0;
  }
  const a = Math.max(0, Math.floor(Number(p.adults) || 0));
  const cRaw = Math.max(0, Math.floor(Number(p.children) || 0));
  const c = p.childEligible ? cRaw : 0;
  const ap =
    p.adultUnit != null && Number.isFinite(Number(p.adultUnit))
      ? Number(p.adultUnit)
      : 0;
  const cp =
    p.childEligible && p.childUnit != null && Number.isFinite(Number(p.childUnit))
      ? Number(p.childUnit)
      : 0;
  return a * ap + c * cp;
}
